import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
  Tabs, Tab, Chip, Divider, TextField, InputAdornment, Button,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Checkbox, Tooltip, Alert, Snackbar, CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
  QuestionAnswer as FAQIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  SupportAgent as OperatorIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Visibility as PublishIcon,
  VisibilityOff as UnpublishIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import { usePermissions } from '../components/RoleBasedRoute';
import apiClient from '../utils/api';

interface HelpArticle {
  id: string;
  type: 'manual' | 'faq';
  title: string;
  content: string;
  category: string | null;
  audience: 'all' | 'admin' | 'operator';
  tags: string[];
  sort_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

// 初期データ（seed用）
const SEED_DATA: Omit<HelpArticle, 'id' | 'created_at' | 'updated_at' | 'is_published'>[] = [
  {
    type: 'manual', title: 'MyTalkとは', sort_order: 0, audience: 'all', tags: ['基本', '概要'], category: null,
    content: `MyTalkは、お客様とリアルタイムでコミュニケーションを取るためのチャットサポートシステムです。\nSMS・メールでお客様を招待し、Webチャットで対応を行います。\n\n■ 主な機能\n・チャットルームの作成・管理\n・SMS / メールによるお客様招待\n・リアルタイムメッセージのやり取り\n・ファイル添付（画像・PDF等）\n・ステータス管理（返信待ち→未対応→対応中→完了）\n・レポート・分析機能`,
  },
  {
    type: 'manual', title: 'ログイン方法', sort_order: 1, audience: 'all', tags: ['基本', 'ログイン'], category: null,
    content: `1. ブラウザでMyTalkのURLにアクセスします\n2. ユーザー名とパスワードを入力します\n3. 「ログイン」ボタンをクリックします\n\n※ パスワードを忘れた場合は管理者にお問い合わせください。`,
  },
  {
    type: 'manual', title: 'チャットルームの作成', sort_order: 2, audience: 'all', tags: ['チャット', '作成'], category: null,
    content: `1. 左サイドバーの「＋新規作成」ボタンをクリック\n2. お客様の情報を入力します\n   ・参加者名（お客様のお名前）\n   ・電話番号（SMS招待する場合）\n   ・メールアドレス（メール招待する場合）\n   ・案件No.（任意）\n3. 招待方法を選択（SMS / メール / 両方）\n4. 「作成」をクリックすると、お客様に招待が送信されます\n\n■ 注意事項\n・電話番号は080/090/070で始まる11桁の番号を入力してください\n・メールアドレスは正しい形式で入力してください`,
  },
  {
    type: 'manual', title: 'メッセージの送受信', sort_order: 3, audience: 'all', tags: ['チャット', 'メッセージ'], category: null,
    content: `■ メッセージ送信\n1. チャット一覧からルームを選択\n2. 画面下部のテキスト入力欄にメッセージを入力\n3. Enterキーまたは送信ボタンで送信\n\n■ ファイル添付\n・添付ボタン（📎アイコン）をクリック\n・画像・PDF等のファイルを選択\n・ファイルが送信されます\n・添付可能サイズ: 約9.5MBまで（画像は自動圧縮されます）\n\n■ 添付ファイルの表示\n・画像ファイル（PNG/JPEG/GIF/WebP）はチャット内にプレビュー表示されます\n・画像をクリック/タップするとフルスクリーンのビューアーが開きます\n・ビューアーではズーム（拡大・縮小）操作が可能です\n・PDF・Office文書（Word/Excel/PowerPoint）もビューアーで閲覧可能です\n・ビューアー非対応のファイルはダウンロードで取得できます\n\n■ SMS気づき通知\n・運営者がメッセージを送信すると、お客様の携帯にSMS通知が届きます\n・同じルームへの通知は5分に1回までに制限されています`,
  },
  {
    type: 'manual', title: 'ステータスの管理', sort_order: 4, audience: 'all', tags: ['ステータス', '管理'], category: null,
    content: `チャットルームには以下のステータスがあります。\n\n【ステータス一覧】\n\n● 返信待ち（初期ステータス）\n  チャットルーム新規作成時の初期ステータスです。\n  お客様からの返信を待っている状態です。\n  運営者がメッセージを送信した場合も、自動的にこのステータスに変わります。\n\n● 未対応\n  お客様がメッセージを送信すると、自動的にこのステータスに変わります。\n  運営者がまだ対応していない状態を示します。\n\n● 対応中\n  運営者が現在対応中の状態です。\n  チャット画面を開いて操作を始めると、このステータスに変更します。\n\n● 保留\n  一時的に対応を保留している状態です。\n  確認事項がある場合や、後日対応する場合に使用します。\n\n● 一時完了\n  一時的に対応を完了とした状態です。保留と同じ性格のステータスです。\n  受信トレイの「全て」にも表示され、「一時完了」カテゴリでも確認できます。\n  お客様がメッセージを送信すると「未対応」に、運営者が送信すると「返信待ち」に自動変更されます。\n\n● 完了\n  対応が完了した状態です。\n  受信トレイの「全て」には表示されず、「完了」カテゴリで確認できます。\n  メッセージ送受信による自動ステータス変更の対象外です。\n\n● 削除\n  不要になったルームです。\n  受信トレイの「全て」には表示されず、「削除」カテゴリで確認できます。\n  ステータスを「削除」に変更する際は、理由の入力を求められます。\n\n【自動ステータス変更】\n\n以下のタイミングでステータスが自動的に変更されます。\n\n・お客様がメッセージを送信 → 「未対応」に自動変更\n・運営者がメッセージを送信 → 「返信待ち」に自動変更\n・60日間操作がないルーム → 「削除」に自動変更\n\n※ 「完了」「削除」のルームは自動変更の対象外です。\n※ 「一時完了」は保留と同じく自動変更の対象です。\n\n【ステータスの手動変更方法】\n\n1. チャット画面右上のステータスドロップダウンをクリック\n2. 変更したいステータスを選択\n3. チャット内にシステムメッセージが表示されます\n\n【サイドバーでの表示】\n\n・「全て」: 未対応・対応中・返信待ち・保留のルームを表示\n・「未対応」: 未対応のルームのみ表示\n・「対応中」: 対応中のルームのみ表示\n・「返信待ち」: 返信待ちのルームのみ表示\n・「保留」: 保留中のルームのみ表示\n・「一時完了」: 一時完了のルームのみ表示（保留の下に配置）\n・「完了」: 完了したルームのみ表示\n・「削除」: 削除されたルームのみ表示`,
  },
  {
    type: 'manual', title: 'タグ・案件No・メモの管理', sort_order: 5, audience: 'all', tags: ['タグ', '案件No', 'メモ'], category: null,
    content: `チャットルームには、対応状況を整理するための情報を付与できます。\nこれらはチャット画面の右側パネルで確認・編集できます。\n\n【固定タグ（商材タグ・対応部署タグ・お問合せタグ）】\n\n管理者がシステム設定で事前に登録したカテゴリ・選択肢から選んで設定します。\n\n● 設定方法\n1. チャットルームを開く\n2. 右側パネルの各タグカテゴリのドロップダウンから選択\n3. 選択と同時に自動保存されます\n\n● タグの種類（初期設定例）\n・商材タグ: BiZiMo光、ライト光、光ギガなど取り扱い商材の分類\n・対応部署タグ: 対応を担当する部署の分類\n・お問合せタグ: お問い合わせ内容の分類（移転、新規、解約など）\n\n● カテゴリ・選択肢の追加・変更（管理者のみ）\n1. 「システム設定」画面を開く\n2. 「固定タグ管理」セクションでカテゴリや選択肢を追加・編集・削除\n\n【カスタムタグ】\n\n自由入力で任意のタグを付けることができます。\n\n● 設定方法\n1. チャットルームを開く\n2. 右側パネルの「タグ」欄に任意のテキストを入力\n3. Enterキーまたは「＋」ボタンで追加\n4. 過去に使用したタグはオートコンプリートで候補表示されます\n5. タグの「×」ボタンで削除できます\n\n【案件No】\n\nお客様の案件番号（例: ASK06135902）を記録できます。\n\n● 設定方法\n1. チャットルーム作成時に入力（任意）\n2. または、チャット画面右側パネルの「案件No」欄に入力\n3. 「案件Noを保存」ボタンをクリック\n\n● 活用\n・チャット一覧の検索で案件Noによる検索が可能\n・レポートのCSV出力にも案件Noが含まれます\n\n【チャットルームメモ】\n\nチャットルームに関する自由記述のメモを残せます。\n\n● 設定方法\n1. チャット画面右側パネルの「チャットルームメモ」欄に入力\n2. 「メモを保存」ボタンをクリック\n\n● 活用例\n・対応の経緯や注意事項の記録\n・引き継ぎ事項のメモ\n・お客様からの要望や確認事項のメモ`,
  },
  {
    type: 'manual', title: 'チャット一覧の検索・ソート', sort_order: 6, audience: 'all', tags: ['検索', 'ソート'], category: null,
    content: `■ 検索機能\nチャット一覧上部の検索窓で以下の項目を検索できます：\n・ルーム名、参加者名、電話番号、メールアドレス\n・案件No.、メモ、最終メッセージ\n\n※ 検索窓横の「?」アイコンにマウスを合わせると検索対象を確認できます\n\n■ ソート機能\nソートボタンで、経過時間の順序を切り替えられます。`,
  },
  {
    type: 'manual', title: 'ファイルの確認・管理', sort_order: 7, audience: 'all', tags: ['ファイル', '添付', 'ビューアー', '管理'], category: null,
    content: `添付ファイルの確認方法と管理機能について説明します。\n\n■ チャット画面でのファイル確認\n・画像ファイルはチャット内にプレビュー表示されます\n・画像をクリックすると、フルスクリーンのビューアーが開きます\n・ビューアーでは拡大・縮小（ズーム）操作ができます\n・PDFファイルはブラウザ内蔵のPDFビューアーで表示されます\n・Office文書（Word/Excel/PowerPoint）はMicrosoft Office Onlineのビューアーで表示されます\n・上記に該当しないファイルはダウンロードになります\n\n■ 右カラムのファイル一覧タブ\nチャット画面の右カラム（顧客情報パネル）に「ファイル一覧」タブがあります。\n\n● 切り替え方法\n1. 右カラム上部のタブアイコンを確認\n2. 人物アイコン（👤）: 顧客情報を表示\n3. クリップアイコン（📎）: ファイル一覧を表示\n\n● ファイル一覧の機能\n・選択中のルームの添付ファイルを新しい順にリスト表示\n・ファイル名・送信者・送信日時・ファイルサイズを確認\n・ファイル名をクリックでビューアーを起動\n・ダウンロードボタンで直接ダウンロード\n\n■ ファイル管理ページ（管理者のみ）\n全ルームの添付ファイルを一覧で確認できる管理ページです。\n\n● アクセス方法\n1. 左メニューの「ファイル管理」をクリック\n\n● 機能\n・全ルームのファイルを日時順にリスト表示\n・ファイル名・ルーム名での検索\n・日付範囲によるフィルタリング\n・ストレージ種別（S3/ローカル）でのフィルタリング\n・ファイルのダウンロード\n\n■ ダウンロード期限\n・お客様: 添付から1週間\n・運営者: 添付から3ヶ月\n・管理者: 無制限`,
  },
  {
    type: 'manual', title: 'アカウント管理', sort_order: 8, audience: 'admin', tags: ['管理', 'アカウント'], category: null,
    content: `管理者はアカウントの作成・編集・削除ができます。\n\n■ アカウント作成\n1. 左メニューの「アカウント管理」をクリック\n2. 「アカウント追加」ボタンをクリック\n3. ユーザー名・表示名・メールアドレス・パスワード・権限を入力\n4. 「作成」をクリック\n\n■ 権限の種類\n・管理者（admin）: 全機能にアクセス可能\n・運営者（operator）: チャット対応・レポート閲覧が可能`,
  },
  {
    type: 'manual', title: 'システム設定', sort_order: 9, audience: 'admin', tags: ['管理', '設定'], category: null,
    content: `管理者のみがアクセスできる設定画面です。\n\n■ 設定可能な項目\n・IP制限: 管理画面にアクセスできるIPアドレスの制限\n・固定タグ: チャットルームに設定できるタグのカテゴリ・選択肢の管理\n・招待URL: お客様招待時のベースURLの設定`,
  },
  {
    type: 'manual', title: 'レポート・分析', sort_order: 10, audience: 'all', tags: ['レポート', '分析'], category: null,
    content: `「分析・レポート」画面では、以下のレポートを確認できます。\n\n■ アカウント一覧\n・各アカウント（運営者）ごとのルーム数・未対応件数\n\n■ ルーム一覧\n・全ルームのステータス・作成日時一覧\n\n■ ルーム詳細レポート\n・ルームごとの開始日時・完了日時・経過時間\n・運営/顧客/合計メッセージ数\n・CSV出力（UTF-8 / Shift-JIS）`,
  },
  {
    type: 'faq', title: 'お客様にSMSが届かない場合は？', sort_order: 0, audience: 'all', tags: [], category: 'SMS',
    content: `以下を確認してください：\n1. 電話番号が正しいか（080/090/070で始まる11桁）\n2. お客様の端末がSMS受信可能か\n3. 招待配信状況が「配信完了」になっているか\n\n配信状況はチャット一覧の各ルームに表示されるアイコンで確認できます。\nそれでも届かない場合は、管理者にお問い合わせください。`,
  },
  {
    type: 'faq', title: 'お客様がチャットに入れない場合は？', sort_order: 1, audience: 'all', tags: [], category: '認証',
    content: `お客様が認証画面で「認証コードを送信」ボタンを押すと、SMSで4桁の認証コードが届きます。\nそのコードを入力するとチャットに参加できます。\n\n認証コードの有効期限は48時間です。期限切れの場合は再度「認証コードを送信」ボタンを押してください。`,
  },
  {
    type: 'faq', title: 'ロック中のルームとは？', sort_order: 2, audience: 'all', tags: [], category: 'チャット',
    content: `他の運営者がチャットルームを開いている場合、そのルームは「ロック中」になります。\nロック中のルームは一覧でオレンジ色の枠線で表示され、ロックアイコンが付きます。\n\nロック中でもルームを閲覧することは可能ですが、メッセージの送信はできません。\n他の運営者がルームを離れるとロックが自動的に解除されます。`,
  },
  {
    type: 'faq', title: 'ステータスを変更したのに一覧に表示されない', sort_order: 3, audience: 'all', tags: [], category: 'ステータス',
    content: `受信トレイのカテゴリ（サイドバー）で表示対象が絞られている可能性があります。\n\n「全て」カテゴリを選択すると、完了・削除以外の全ステータスのルームが表示されます（一時完了も含まれます）。\n完了したルームは「完了」カテゴリで確認できます。`,
  },
  {
    type: 'faq', title: 'CSVレポートの文字化けを直すには？', sort_order: 4, audience: 'all', tags: [], category: 'レポート',
    content: `CSVをExcelで開く際に文字化けする場合は、Shift-JIS形式でダウンロードしてください。\n\nレポート画面の「CSV出力（Shift-JIS）」ボタンを使うと、Excelで正しく表示されます。\nMac の Numbers やGoogle スプレッドシートでは、UTF-8形式のCSVが正しく表示されます。`,
  },
  {
    type: 'faq', title: '運営者アカウントを追加するには？', sort_order: 5, audience: 'admin', tags: [], category: 'アカウント',
    content: `管理者権限が必要です。\n\n1. 「アカウント管理」画面を開く\n2. 「アカウント追加」をクリック\n3. 必要情報を入力して作成\n\n運営者には以下の権限が付与されます：\n・チャットルームの作成・対応\n・レポートの閲覧`,
  },
  {
    type: 'faq', title: 'IP制限を設定するには？', sort_order: 6, audience: 'admin', tags: [], category: '設定',
    content: `管理者権限が必要です。\n\n1. 「システム設定」画面を開く\n2. 「IPアクセス制限」セクションで許可するIPアドレスを追加\n3. 設定を保存\n\n※ 自分のIPアドレスを除外しないように注意してください。`,
  },
  {
    type: 'faq', title: '固定タグの選択肢を追加・変更するには？', sort_order: 7, audience: 'admin', tags: [], category: 'タグ',
    content: `管理者権限が必要です。\n\n1. 「システム設定」画面を開く\n2. 「固定タグ管理」セクションを確認\n3. カテゴリの追加: カテゴリ名を入力して「追加」\n4. 選択肢の追加: カテゴリを選択し、選択肢名を入力して「追加」\n\n既存のカテゴリ・選択肢は名前の編集や削除も可能です。\nカテゴリを削除すると、配下の選択肢も全て削除されます。`,
  },
  {
    type: 'faq', title: '案件Noでチャットルームを検索するには？', sort_order: 8, audience: 'all', tags: [], category: 'タグ',
    content: `チャット一覧上部の検索窓に案件No（例: ASK06135902）を入力すると、該当するルームが絞り込まれます。\n\n案件Noは部分一致で検索されるため、「ASK061」のように途中まで入力しても該当するルームが表示されます。`,
  },
  {
    type: 'faq', title: 'メモやタグが保存されない場合は？', sort_order: 9, audience: 'all', tags: [], category: 'タグ',
    content: `以下を確認してください。\n\n● チャットルームメモ・案件No\n・入力後に「メモを保存」「案件Noを保存」ボタンをクリックしていますか？\n・ボタンが「保存しました！」に変わればOKです\n\n● 固定タグ（商材・対応部署・お問合せ）\n・ドロップダウンから選択すると自動保存されます\n・ボタンのクリックは不要です\n\n● カスタムタグ\n・入力後にEnterキーまたは「＋」ボタンで追加すると自動保存されます\n\nそれでも保存されない場合は、ページを再読み込みして再度お試しください。`,
  },
];

const EMPTY_FORM: Omit<HelpArticle, 'id' | 'created_at' | 'updated_at'> = {
  type: 'manual', title: '', content: '', category: '', audience: 'all',
  tags: [], sort_order: 0, is_published: true,
};

const HelpManual: React.FC = () => {
  const { isAdmin } = usePermissions();
  const admin = isAdmin();
  const [tab, setTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<(typeof EMPTY_FORM & { id?: string }) | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.getHelpArticles({ includeUnpublished: admin });
      if (res.success) {
        setArticles(res.articles);
        // 初回: テーブルが空なら初期データを投入
        if (res.articles.length === 0 && admin) {
          const seedRes = await apiClient.seedHelpArticles(SEED_DATA);
          if (seedRes.success && seedRes.seeded) {
            const refetch = await apiClient.getHelpArticles({ includeUnpublished: true });
            if (refetch.success) setArticles(refetch.articles);
          }
        }
      }
    } catch (e) {
      console.error('記事取得エラー:', e);
    }
    setLoading(false);
  }, [admin]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const manuals = articles.filter(a => a.type === 'manual');
  const faqs = articles.filter(a => a.type === 'faq');

  const currentList = tab === 0 ? manuals : faqs;
  const filtered = currentList.filter(a =>
    !searchTerm ||
    a.title.includes(searchTerm) ||
    a.content.includes(searchTerm) ||
    a.category?.includes(searchTerm) ||
    a.tags?.some(t => t.includes(searchTerm))
  );

  const faqCategories = Array.from(new Set(filtered.filter(a => a.type === 'faq').map(a => a.category).filter(Boolean))) as string[];

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // --- 編集操作 ---

  const openCreateDialog = () => {
    setEditingArticle({ ...EMPTY_FORM, type: tab === 0 ? 'manual' : 'faq', sort_order: currentList.length });
    setTagInput('');
    setDialogOpen(true);
  };

  const openEditDialog = (article: HelpArticle) => {
    setEditingArticle({ ...article });
    setTagInput(article.tags?.join(', ') || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingArticle) return;
    try {
      const payload = {
        ...editingArticle,
        tags: tagInput ? tagInput.split(',').map(t => t.trim()).filter(Boolean) : [],
        category: editingArticle.category || null,
      };

      if (editingArticle.id) {
        const res = await apiClient.updateHelpArticle(editingArticle.id, payload);
        if (res.success) { showSnack('更新しました'); }
        else { showSnack(res.message || '更新に失敗', 'error'); return; }
      } else {
        const res = await apiClient.createHelpArticle(payload);
        if (res.success) { showSnack('作成しました'); }
        else { showSnack(res.message || '作成に失敗', 'error'); return; }
      }
      setDialogOpen(false);
      fetchArticles();
    } catch (e) {
      showSnack('保存に失敗しました', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('この記事を削除しますか？')) return;
    try {
      const res = await apiClient.deleteHelpArticle(id);
      if (res.success) { showSnack('削除しました'); fetchArticles(); }
      else showSnack('削除に失敗', 'error');
    } catch { showSnack('削除に失敗', 'error'); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size}件の記事を削除しますか？`)) return;
    try {
      const res = await apiClient.bulkHelpAction({ action: 'delete', ids: Array.from(selectedIds) });
      if (res.success) { showSnack(res.message); setSelectedIds(new Set()); fetchArticles(); }
      else showSnack('一括削除に失敗', 'error');
    } catch { showSnack('一括削除に失敗', 'error'); }
  };

  const handleBulkPublish = async (publish: boolean) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await apiClient.bulkHelpAction({ action: publish ? 'publish' : 'unpublish', ids: Array.from(selectedIds) });
      if (res.success) { showSnack(res.message); setSelectedIds(new Set()); fetchArticles(); }
      else showSnack('一括操作に失敗', 'error');
    } catch { showSnack('一括操作に失敗', 'error'); }
  };

  const handleMoveOrder = async (article: HelpArticle, direction: -1 | 1) => {
    const list = tab === 0 ? manuals : faqs;
    const idx = list.findIndex(a => a.id === article.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= list.length) return;

    const orders = [
      { id: list[idx].id, sort_order: list[swapIdx].sort_order },
      { id: list[swapIdx].id, sort_order: list[idx].sort_order },
    ];

    try {
      await apiClient.bulkHelpAction({ action: 'reorder', orders });
      fetchArticles();
    } catch { showSnack('並び替えに失敗', 'error'); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(a => a.id)));
    }
  };

  // --- 描画 ---
  const renderArticle = (article: HelpArticle, idx: number) => {
    const isSelected = selectedIds.has(article.id);

    return (
      <Accordion key={article.id} defaultExpanded={idx === 0 && !editMode}
        sx={{ mb: 1, opacity: article.is_published ? 1 : 0.5, border: isSelected ? '2px solid #1976d2' : 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {editMode && (
              <Checkbox
                checked={isSelected}
                onChange={() => toggleSelect(article.id)}
                onClick={(e) => e.stopPropagation()}
                size="small"
              />
            )}
            <Typography sx={{ fontWeight: 600, flex: 1 }}>
              {article.type === 'faq' ? 'Q. ' : ''}{article.title}
            </Typography>
            {!article.is_published && (
              <Chip label="非公開" size="small" color="default" variant="outlined" sx={{ height: 22 }} />
            )}
            {article.audience === 'admin' && (
              <Chip label="管理者" size="small" color="error" variant="outlined" sx={{ height: 22 }} />
            )}
            {article.audience === 'operator' && (
              <Chip label="運営者" size="small" color="primary" variant="outlined" sx={{ height: 22 }} />
            )}
            {article.tags?.map(tag => (
              <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
            ))}
            {editMode && (
              <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                <Tooltip title="上に移動">
                  <IconButton size="small" onClick={() => handleMoveOrder(article, -1)} disabled={idx === 0}>
                    <ArrowUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="下に移動">
                  <IconButton size="small" onClick={() => handleMoveOrder(article, 1)} disabled={idx === filtered.length - 1}>
                    <ArrowDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="編集">
                  <IconButton size="small" onClick={() => openEditDialog(article)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="削除">
                  <IconButton size="small" onClick={() => handleDelete(article.id)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {article.category && (
            <Chip label={article.category} size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: 'text.secondary' }}>
            {article.content}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  };

  const userRole = admin ? 'admin' : 'operator';

  return (
    <AdminLayout>
      <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <MenuBookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            ヘルプ・マニュアル
          </Typography>
          <Chip
            icon={userRole === 'admin' ? <AdminIcon sx={{ fontSize: 16 }} /> : <OperatorIcon sx={{ fontSize: 16 }} />}
            label={userRole === 'admin' ? '管理者' : '運営者'}
            size="small"
            color={userRole === 'admin' ? 'error' : 'primary'}
            variant="outlined"
          />
          <Box sx={{ flex: 1 }} />
          {admin && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={editMode ? 'contained' : 'outlined'}
                size="small"
                onClick={() => { setEditMode(!editMode); setSelectedIds(new Set()); }}
              >
                {editMode ? '編集モード終了' : '編集モード'}
              </Button>
            </Box>
          )}
        </Box>

        {/* 検索 */}
        <TextField
          fullWidth
          placeholder="マニュアル・FAQを検索..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* タブ */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelectedIds(new Set()); }} sx={{ flex: 1 }}>
            <Tab icon={<MenuBookIcon />} iconPosition="start" label={`マニュアル (${manuals.length})`} />
            <Tab icon={<FAQIcon />} iconPosition="start" label={`FAQ (${faqs.length})`} />
          </Tabs>
          {editMode && (
            <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={openCreateDialog}>
              新規追加
            </Button>
          )}
        </Box>

        {/* 一括操作バー */}
        {editMode && (
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f5f5f5', flexWrap: 'wrap' }}>
            <Checkbox
              checked={selectedIds.size > 0 && selectedIds.size === filtered.length}
              indeterminate={selectedIds.size > 0 && selectedIds.size < filtered.length}
              onChange={toggleSelectAll}
              size="small"
            />
            <Typography variant="body2" sx={{ mr: 1 }}>
              {selectedIds.size > 0 ? `${selectedIds.size}件選択中` : '全選択'}
            </Typography>
            <Button size="small" variant="outlined" color="error" disabled={selectedIds.size === 0}
              startIcon={<DeleteIcon />} onClick={handleBulkDelete}>
              一括削除
            </Button>
            <Button size="small" variant="outlined" disabled={selectedIds.size === 0}
              startIcon={<PublishIcon />} onClick={() => handleBulkPublish(true)}>
              一括公開
            </Button>
            <Button size="small" variant="outlined" disabled={selectedIds.size === 0}
              startIcon={<UnpublishIcon />} onClick={() => handleBulkPublish(false)}>
              一括非公開
            </Button>
          </Paper>
        )}

        {/* コンテンツ */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {searchTerm ? '検索条件に一致する記事がありません' : '記事がありません。管理者が追加してください。'}
          </Alert>
        ) : tab === 0 ? (
          filtered.map((article, idx) => renderArticle(article, idx))
        ) : (
          faqCategories.length > 0 ? (
            faqCategories.map(category => (
              <Box key={category} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                  {category}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {filtered.filter(a => a.category === category).map((article, idx) => renderArticle(article, idx))}
              </Box>
            ))
          ) : (
            filtered.map((article, idx) => renderArticle(article, idx))
          )
        )}

        {/* カテゴリなしFAQ */}
        {tab === 1 && !loading && filtered.filter(a => !a.category).length > 0 && faqCategories.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
              その他
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {filtered.filter(a => !a.category).map((article, idx) => renderArticle(article, idx))}
          </Box>
        )}
      </Box>

      {/* 編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingArticle?.id ? '記事を編集' : '新規記事作成'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>種類</InputLabel>
              <Select
                value={editingArticle?.type || 'manual'}
                label="種類"
                onChange={(e) => setEditingArticle(prev => prev ? { ...prev, type: e.target.value as 'manual' | 'faq' } : prev)}
              >
                <MenuItem value="manual">マニュアル</MenuItem>
                <MenuItem value="faq">FAQ</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>対象</InputLabel>
              <Select
                value={editingArticle?.audience || 'all'}
                label="対象"
                onChange={(e) => setEditingArticle(prev => prev ? { ...prev, audience: e.target.value as 'all' | 'admin' | 'operator' } : prev)}
              >
                <MenuItem value="all">全員</MenuItem>
                <MenuItem value="admin">管理者のみ</MenuItem>
                <MenuItem value="operator">運営者のみ</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editingArticle?.is_published ?? true}
                  onChange={(e) => setEditingArticle(prev => prev ? { ...prev, is_published: e.target.checked } : prev)}
                />
              }
              label="公開"
            />
          </Box>
          <TextField
            label="タイトル"
            fullWidth
            size="small"
            value={editingArticle?.title || ''}
            onChange={(e) => setEditingArticle(prev => prev ? { ...prev, title: e.target.value } : prev)}
          />
          {editingArticle?.type === 'faq' && (
            <TextField
              label="カテゴリ"
              fullWidth
              size="small"
              placeholder="例: SMS, 認証, チャット, レポート"
              value={editingArticle?.category || ''}
              onChange={(e) => setEditingArticle(prev => prev ? { ...prev, category: e.target.value } : prev)}
            />
          )}
          <TextField
            label="内容"
            fullWidth
            multiline
            minRows={8}
            maxRows={20}
            value={editingArticle?.content || ''}
            onChange={(e) => setEditingArticle(prev => prev ? { ...prev, content: e.target.value } : prev)}
          />
          <TextField
            label="タグ（カンマ区切り）"
            fullWidth
            size="small"
            placeholder="例: 基本, 概要"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!editingArticle?.title || !editingArticle?.content}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(prev => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  );
};

export default HelpManual;
